import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineMapPin,
  HiOutlineXMark,
  HiOutlinePencilSquare,
  HiOutlineTrash,
  HiOutlinePlus,
  HiOutlineCheckCircle,
  HiOutlinePhone,
} from "react-icons/hi2";
import toast from "react-hot-toast";
import SummaryApi from "../common";

const UserAddress = ({ userId, onAddressSelect, onAddressUpdate }) => { 
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [typeError, setTypeError] = useState("");

  const [formData, setFormData] = useState({
    user_name: "",
    user_mobile: "",
    flat_number: "",
    street_name: "",
    land_mark: "",
    address_type: "",
    city: "",
    state: "",
    pincode: "",
    address: "",
    is_default: 0,
  });
const fetchAddresses = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = await fetch(SummaryApi.getUserAddressbyId.url, {
        method: SummaryApi.getUserAddressbyId.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const result = await response.json();
      if (result.status) {
        const addressList = result.data || [];
        setAddresses(addressList);
        
        // Push the whole list to the Cart component
        if (onAddressUpdate) onAddressUpdate(addressList);

        // Find default and push to Cart component
        const defaultAddr = addressList.find((a) => a.is_default === 1) || addressList[0];
        if (defaultAddr && onAddressSelect) {
          onAddressSelect(defaultAddr.address_id);
        }
      }
    } catch (error) {
      console.error("Failed to load addresses", error);
    } finally {
      setLoading(false);
    }
  }, [userId, onAddressSelect, onAddressUpdate]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const handleTypeChange = (val) => {
    setFormData((prev) => ({ ...prev, address_type: val }));
    const isDuplicate = addresses.some(
      (addr) =>
        addr.address_type.toLowerCase() === val.toLowerCase() &&
        addr.address_id !== formData.address_id
    );
    if (isDuplicate && val.trim() !== "") {
      setTypeError("address name is already used. Please choose another");
    } else {
      setTypeError("");
    }
  };

  const handleOpenModal = (address = null) => {
    setTypeError("");
    if (address) {
      setEditMode(true);
      setFormData({ ...address });
    } else {
      setEditMode(false);
      setFormData({
        user_name: "",
        user_mobile: "",
        flat_number: "",
        street_name: "",
        land_mark: "",
        address_type: "",
        city: "",
        state: "",
        pincode: "",
        address: "",
        is_default: addresses.length === 0 ? 1 : 0,
      });
    }
    setIsModalOpen(true);
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    if (typeError) return;
    const isEditing = !!formData.address_id;
    const apiConfig = isEditing ? SummaryApi.addressUpdate : SummaryApi.addressCreate;

    try {
      const response = await fetch(apiConfig.url, {
        method: apiConfig.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, user_id: userId }),
      });
      const result = await response.json();
      if (result.status) {
        toast.success(isEditing ? "Address updated!" : "Address saved!");
        fetchAddresses();
        setIsModalOpen(false);
      } else {
        toast.error(result.message || "Failed to save address");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      const response = await fetch(SummaryApi.defaultAddressUpdate.url, {
        method: SummaryApi.defaultAddressUpdate.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, address_id: addressId }),
      });
      const result = await response.json();
      if (result.status) {
        toast.success("Address selected!");
        
        // Update selection in parent immediately
        if (onAddressSelect) onAddressSelect(addressId);
        
        // Re-fetch to sync everything
        fetchAddresses();
      }
    } catch (error) {
      toast.error("Error selecting address");
    }
  };


  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm("Are you sure you want to delete this address?")) return;
    try {
      const response = await fetch(SummaryApi.addressDelete.url, {
        method: SummaryApi.addressDelete.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address_id: addressId }),
      });
      const result = await response.json();
      if (result.status) {
        toast.success("Address removed");
        fetchAddresses();
      }
    } catch (error) {
      toast.error("Error deleting address");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/70 backdrop-blur-md border border-white p-6 md:p-8 rounded-[2.5rem] shadow-xl max-w-4xl mx-auto"
    >
      
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-rose-500"></div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* SCROLLABLE LIST - Height fixed for ~2 cards */}
            <div className="max-h-[285px] overflow-y-auto pr-2 custom-scrollbar flex flex-col gap-4">
              {addresses.map((addr) => (
                <div
                  key={addr.address_id}
                  onClick={() => handleSetDefault(addr.address_id)}
                  className={`relative cursor-pointer group flex items-center gap-4 p-5 rounded-[2rem] border-2 transition-all duration-300 ${
                    addr.is_default
                      ? "border-rose-500 bg-rose-50/40 shadow-md shadow-rose-100"
                      : "border-gray-100 bg-white hover:border-rose-200"
                  }`}
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-rose-500 rounded-2xl flex items-center justify-center text-white shadow-lg">
                    <HiOutlineMapPin size={24} />
                  </div>

                  <div className="flex-grow">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-rose-950 font-black uppercase text-sm">
                        {addr.user_name}
                      </span>
                      <span className="bg-rose-100 text-rose-600 text-[8px] font-black uppercase px-2 py-0.5 rounded-md">
                        {addr.address_type || "Other"}
                      </span>
                    </div>
                    <p className="text-gray-500 text-[11px] font-semibold leading-tight line-clamp-2">
                      {addr.flat_number}, {addr.street_name}, {addr.city}, {addr.state} - {addr.pincode}
                    </p>
                    <div className="flex items-center gap-1 mt-1 text-rose-600">
                      <HiOutlinePhone size={12} />
                      <span className="text-[11px] font-bold">{addr.user_mobile}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={(e) => { e.stopPropagation(); handleOpenModal(addr); }} className="p-1 text-blue-400 hover:text-blue-600"><HiOutlinePencilSquare size={18} /></button>
                      {!addr.is_default && (
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteAddress(addr.address_id); }} className="p-1 text-rose-300 hover:text-rose-500"><HiOutlineTrash size={18} /></button>
                      )}
                    </div>
                    {addr.is_default === 1 && <HiOutlineCheckCircle className="text-rose-500" size={26} />}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => handleOpenModal()}
              className="w-full flex items-center justify-center gap-2 py-5 rounded-[2rem] border-2 border-dashed border-rose-200 text-rose-500 hover:bg-rose-50 transition-colors"
            >
              <HiOutlinePlus size={18} className="stroke-[3]" />
              <span className="text-xs font-black uppercase tracking-widest">Add New Address</span>
            </button>
          </div>
        )}
      </div>

<AnimatePresence>
  {isModalOpen && (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setIsModalOpen(false)}
        className="absolute inset-0 bg-rose-950/40 backdrop-blur-sm"
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-[3rem] shadow-2xl p-7 mt-[35%]"
      >
        <button
          onClick={() => setIsModalOpen(false)}
          className="absolute top-6 right-7 text-rose-300 hover:text-rose-500"
        >
          <HiOutlineXMark size={24} />
        </button>

        <h2 className="text-2xl font-black text-rose-950 uppercase tracking-tight mb-5">
          {editMode ? "Edit Address" : "Add New Address"}
        </h2>

        <form onSubmit={handleAddressSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2.5">
            <FormInput label="Full Name" value={formData.user_name} onChange={(v) => setFormData({ ...formData, user_name: v })} required />
            <FormInput
              label="Mobile"
              type="tel"
              maxLength={10}
              value={formData.user_mobile}
              onChange={(v) => setFormData({ ...formData, user_mobile: v.replace(/\D/g, "") })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <FormInput label="Flat / House No" value={formData.flat_number} onChange={(v) => setFormData({ ...formData, flat_number: v })} required />
            <FormInput label="Street Name" value={formData.street_name} onChange={(v) => setFormData({ ...formData, street_name: v })} required />
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <FormInput label="Landmark" value={formData.land_mark} onChange={(v) => setFormData({ ...formData, land_mark: v })} />
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-black text-rose-400 uppercase tracking-widest ml-1">Type</label>
              <input
                className={`w-full bg-rose-50/50 border ${typeError ? 'border-red-400' : 'border-rose-100'} rounded-xl px-3 py-2.5 text-sm text-rose-950 font-bold outline-none focus:ring-2 ring-rose-200 placeholder:font-normal`}
                placeholder="e.g. Home, Work"
                value={formData.address_type}
                onChange={(e) => handleTypeChange(e.target.value)}
                required
              />
              {typeError && <span className="text-[9px] text-red-500 font-bold ml-1 mt-0.5">{typeError}</span>}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2.5">
            <FormInput label="City" value={formData.city} onChange={(v) => setFormData({ ...formData, city: v })} required />
            <FormInput label="State" value={formData.state} onChange={(v) => setFormData({ ...formData, state: v })} required />
            <FormInput
              label="Pincode"
              type="tel"
              maxLength={6}
              value={formData.pincode}
              onChange={(v) => setFormData({ ...formData, pincode: v.replace(/\D/g, "") })}
              required
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-black text-rose-400 uppercase tracking-widest ml-1">Full Address / Directions</label>
            <textarea
              rows="2"
              className="w-full bg-rose-50/50 border border-rose-100 rounded-xl px-3 py-2.5 text-sm text-rose-950 font-bold outline-none focus:ring-2 ring-rose-200 resize-none"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            
            />
          </div>

          <button
            type="submit"
            disabled={!!typeError}
            className={`w-full mt-3 ${typeError ? 'bg-gray-300' : 'bg-rose-600 hover:bg-rose-700'} text-white py-3.5 rounded-2xl font-black uppercase text-xs tracking-widest shadow-lg transition-all active:scale-95`}
          >
            {editMode ? "Update Address" : "Save Address"}
          </button>
        </form>
      </motion.div>
    </div>
  )}
</AnimatePresence>


      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #fb7185; border-radius: 10px; }
      `}</style>
    </motion.div>
  );
};

const FormInput = ({ label, value, onChange, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-black text-rose-400 uppercase tracking-widest ml-1">{label}</label>
    <input {...props} className="w-full bg-rose-50/50 border border-rose-100 rounded-xl p-3 text-sm text-rose-950 font-bold outline-none focus:ring-2 ring-rose-200" value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

export default UserAddress;